import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Zap, Loader2, Stethoscope, Plane, MapPin } from "lucide-react";

function Countdown({ expiresAt }) {
  const [left, setLeft] = useState("");
  useEffect(() => {
    const tick = () => {
      const ms = new Date(expiresAt) - new Date();
      if (ms <= 0) { setLeft("expired"); return; }
      const m = Math.floor(ms / 60000), s = Math.floor((ms % 60000) / 1000);
      setLeft(`${m}:${String(s).padStart(2, "0")}`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [expiresAt]);
  return <span className="font-mono font-bold">{left}</span>;
}

export const PriorityReservations = () => {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    api.get("/routing/reservations").then(({ data }) => setRows(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); const id = setInterval(load, 20000); return () => clearInterval(id); }, [load]);

  const act = async (rid, action) => {
    setBusy(rid);
    try {
      const { data } = await api.post(`/routing/reservations/${rid}/${action}`);
      if (action === "accept") toast.success(`Case accepted for client engagement — receipt ${data.transaction_id}. The client will receive your engagement documents.`);
      else toast.success("Reservation declined — offered to the next attorney.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Could not ${action} reservation`);
      load();
    } finally { setBusy(null); }
  };

  if (!rows.length) return null;

  return (
    <div className="mt-5 space-y-3" data-testid="priority-reservations">
      {rows.map((r) => (
        <div key={r.reservation_id} className="bg-amber-50 border border-amber-200 rounded-2xl p-5" data-testid={`reservation-${r.reservation_id}`}>
          <div className="flex items-center gap-2 text-amber-700 text-xs font-bold uppercase tracking-wide">
            <Zap className="w-4 h-4" /> {r.label}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-600">
            <span className="font-mono text-xs text-slate-400">{r.case_number}</span>
            <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {[r.city, r.state].filter(Boolean).join(", ") || "—"}</span>
            {r.overall_grade && <span>Score {r.overall_grade} ({r.overall_score})</span>}
            {r.trust_score != null && <span>Trust {r.trust_score}</span>}
            <span className="flex items-center gap-1"><Stethoscope className="w-3.5 h-3.5" /> {r.rn_case_verified ? "RN Verified" : "RN pending"}</span>
            <span className="flex items-center gap-1"><Plane className="w-3.5 h-3.5" /> {r.drone_decision === "drone_approved" ? "Drone approved" : (r.drone_decision || "No drone")}</span>
          </div>
          <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
            <div className="text-xs text-amber-700">Exclusive reservation expires in <Countdown expiresAt={r.expires_at} /> · Fee ${Number(r.fee).toLocaleString()}</div>
            <div className="flex gap-2">
              <button onClick={() => act(r.reservation_id, "decline")} disabled={busy === r.reservation_id} data-testid={`reservation-decline-${r.reservation_id}`}
                className="px-4 py-2 rounded-full bg-white border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50">Decline</button>
              <button onClick={() => act(r.reservation_id, "accept")} disabled={busy === r.reservation_id} data-testid={`reservation-accept-${r.reservation_id}`}
                className="px-4 py-2 rounded-full bg-navy text-white text-sm font-semibold hover:opacity-90 flex items-center gap-2">
                {busy === r.reservation_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />} Accept for Client Engagement
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};
